import { useMemo } from "react";
import { Text, Stack } from "@mantine/core";
import { useIcdCodes } from "../../../hooks/useReferenceData";
import { IcdCodeRow } from "./IcdCodeRow";

interface SpecimenDiagnosisData {
  specimenId: string;
  icdCode: string | null;
  icdCodes?: string[];
}

interface IcdCodesWidgetProps {
  specimens: any[];
  diagnosisDataMap: Map<string, SpecimenDiagnosisData>;
  onCodesChange?: (specimenId: string, codes: string[]) => void;
  isReadOnly?: boolean;
}

function resolveCodes(diagData: SpecimenDiagnosisData | undefined): string[] {
  if (diagData?.icdCodes?.length) return diagData.icdCodes;
  return diagData?.icdCode ? [diagData.icdCode] : [];
}

export function IcdCodesWidget({
  specimens,
  diagnosisDataMap,
  onCodesChange,
  isReadOnly = false,
}: IcdCodesWidgetProps) {
  const { data: allIcdCodes = [] } = useIcdCodes();

  const sorted = useMemo(
    () =>
      [...specimens].sort((a: any, b: any) =>
        (a.number || "").localeCompare(b.number || "")
      ),
    [specimens]
  );

  const options = useMemo(
    () =>
      allIcdCodes.map((code) => ({
        value: code.code,
        label: code.label,
      })),
    [allIcdCodes]
  );

  if (sorted.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No specimens
      </Text>
    );
  }

  return (
    <Stack gap={6}>
      {sorted.map((specimen: any) => (
        <IcdCodeRow
          key={specimen.id}
          specimen={specimen}
          currentCodes={resolveCodes(diagnosisDataMap.get(specimen.id))}
          options={options}
          isReadOnly={isReadOnly}
          onCodesChange={onCodesChange}
        />
      ))}
    </Stack>
  );
}
